import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, ChevronDown, Copy, FileDown, FileType, Link2, FileSpreadsheet, Presentation, Loader2 } from 'lucide-react';
import { AudienceLevel, Cohort, LEVEL_LABELS } from '../../types/pathway';
import { formatHours, totalHours } from '../../utils/pathwayMatching';
import { PathwayButton } from './PathwayButton';
import { ShareFormatPreview } from './ShareFormatPreview';
import { DocumentViewer } from './DocumentViewer';
import { PrintSurface, printPathwayDocument } from './PrintSurface';
import { buildPathwayCsv, csvFileName, downloadCsv } from '../../utils/pathwayCsv';
import { buildPathwayWord, downloadWord, wordFileName } from '../../utils/pathwayWord';
import { downloadPathwayPowerPoint } from '../../utils/pathwayPowerPoint';
interface ShareModalProps {
  open: boolean;
  pathwayName: string;
  /** Set in the pathway panel; editable here as a last check before sending. */
  company: string;
  items: string[];
  audience: AudienceLevel | 'any';
  cohort: Cohort | null;
  onCompanyChange: (company: string) => void;
  onClose: () => void;
}
const CORE_FIELDS = ['Description', 'Relevance', 'Expected outcomes'];
const EXTRA_FIELDS = ['Category', 'Topics', 'Skill Module ID'];
type FormatId = 'pdf' | 'link' | 'word' | 'csv' | 'powerpoint';
const FORMATS: {
  id: FormatId;
  label: string;
  blurb: string;
  action: string;
  icon: typeof FileDown;
}[] = [{
  id: 'link',
  label: 'Shareable link',
  blurb: 'Send it and see when they open it',
  action: 'Create Shareable Link',
  icon: Link2
}, {
  id: 'powerpoint',
  label: 'PowerPoint',
  blurb: 'Editable slide deck for a live discussion',
  action: 'Download PowerPoint',
  icon: Presentation
}, {
  id: 'csv',
  label: 'CSV',
  blurb: 'Import file for their L&D team',
  action: 'Download CSV',
  icon: FileSpreadsheet
}, {
  id: 'word',
  label: 'Word',
  blurb: 'Editable, for your own proposal',
  action: 'Download Word',
  icon: FileType
}, {
  id: 'pdf',
  label: 'PDF',
  blurb: 'Branded leave-behind for the prospect',
  action: 'Save as PDF',
  icon: FileDown
}];
export function ShareModal({
  open,
  pathwayName,
  company,
  items,
  audience,
  cohort,
  onCompanyChange,
  onClose
}: ShareModalProps) {
  const [extras, setExtras] = useState<string[]>([]);
  const [showExtras, setShowExtras] = useState(false);
  const [format, setFormat] = useState<FormatId>('pdf');
  const [status, setStatus] = useState<'idle' | 'working' | 'linked'>('idle');
  const [copied, setCopied] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onClose]);
  if (!open) return null;

  // Keep the "included" list in the dialog's own order, not click order.
  const selectedExtras = EXTRA_FIELDS.filter(field => extras.includes(field));
  const link = `https://phoenix.edu/pd/pathway/${(pathwayName || 'pathway').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}-8f2c`;
  const toggleExtra = (field: string) => setExtras(prev => prev.includes(field) ? prev.filter(f => f !== field) : [...prev, field]);
  const activeFormat = FORMATS.find(f => f.id === format) ?? FORMATS[0];
  const ActiveIcon = activeFormat.icon;
  const createLink = () => {
    setStatus('working');
    window.setTimeout(() => setStatus('linked'), 700);
  };
  const runFormat = () => {
    if (format === 'pdf') return printPathwayDocument(pathwayName);
    if (format === 'word') return handleDownloadWord();
    if (format === 'csv') return handleDownloadCsv();
    if (format === 'powerpoint') return handleDownloadPowerPoint();
    return createLink();
  };
  const copyLink = () => {
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };
  const handleDownloadWord = () => {
    downloadWord(wordFileName(pathwayName), buildPathwayWord({
      pathwayName,
      company,
      cohort,
      audience,
      items,
      fields: selectedExtras
    }));
  };
  const handleDownloadCsv = () => {
    downloadCsv(csvFileName(pathwayName), buildPathwayCsv({
      pathwayName,
      company,
      cohort,
      audience,
      items,
      fields: selectedExtras
    }));
  };
  const handleDownloadPowerPoint = () => downloadPathwayPowerPoint({
    pathwayName,
    company,
    audience,
    items
  });
  return <>
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 p-4" role="dialog" aria-modal="true" aria-labelledby="share-title" onMouseDown={event => {
      if (event.target === event.currentTarget) onClose();
    }} data-id="element-2008" data-mp="2242wh6fskbtb">
      <motion.div onMouseDown={event => event.stopPropagation()} initial={{
        opacity: 0,
        y: 12,
        scale: 0.96
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} transition={{
        duration: 0.22,
        ease: [0.23, 1, 0.32, 1]
      }} className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-white shadow-lg" data-id="element-2009" data-mp="3mlu728xj0ptn">
        <div className="flex items-start justify-between border-b border-gray-200 p-6" data-id="element-2010" data-mp="e4haa3hjh3at">
          <div data-id="element-2011" data-mp="9660fsr8ussl">
            <h2 id="share-title" className="text-xl font-bold text-gray-900" data-id="element-2012" data-mp="96o4vco28dbl">
              Review and send
            </h2>
            <p className="mt-1 text-sm text-gray-600" data-id="element-2013" data-mp="n4cm8aix52bw">
              {items.length} modules · {formatHours(totalHours(items))} ·{' '}
              {audience === 'any' ? 'Mixed audience' : LEVEL_LABELS[audience]}
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-sm font-semibold text-gray-700 underline transition-colors hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#30718D]" data-id="element-2014" data-mp="2k024orxsgv65">
            Close
          </button>
        </div>

        <div className="grid flex-1 gap-6 overflow-y-auto p-6 lg:grid-cols-2" data-id="element-2015" data-mp="xsyz6dlbavkc">
          <div className="space-y-5" data-id="element-2016" data-mp="11w53hfov6co6">
            <label className="block" data-id="element-2017" data-mp="11x6rsluw4vnu">
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-2018" data-mp="16uq5nknof6f9">
                Company
              </span>
              <input value={company} onChange={e => onCompanyChange(e.target.value)} placeholder="Meridian Health" className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#30718D]" data-id="element-2019" data-mp="1bsi6532e00v2" />
              <span className="mt-1 block text-xs text-gray-500" data-id="element-2020" data-mp="1ujrjjnfa39rx">
                Appears on the cover of the PDF and the shared page.
              </span>
            </label>

            <div data-id="element-2021" data-mp="3a38lpdma2qop">
              <span className="block text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-2022" data-mp="3a2qsyqytqulj">
                Included for every module
              </span>
              <ul className="mt-2 space-y-1.5" data-id="element-2023" data-mp="363rfuj2xvrj0">
                {[...CORE_FIELDS, ...selectedExtras].map(field => <li key={field} className="flex items-center gap-2 text-sm text-gray-700" data-id="element-2024" data-mp="1nfe5o9xcpjs">
                    <span className="flex h-4 w-4 items-center justify-center rounded bg-[#007558]" data-id="element-2025" data-mp="3trrgqbtysybb">
                      <Check size={11} className="text-white" data-id="element-2026" data-mp="3oyyd5ra8fw0t" />
                    </span>
                    {field}
                  </li>)}
              </ul>
              <p className="mt-2 text-xs text-gray-500" data-id="element-2027" data-mp="lchi5dpaab8a">
                The first three answer what a prospect actually asks. Extra
                fields carry through to the document, CSV, and shared page.
              </p>

              <button type="button" onClick={() => setShowExtras(!showExtras)} aria-expanded={showExtras} className="mt-3 flex items-center gap-1 text-sm font-medium text-[#30718D] transition-colors hover:text-[#265a70]" data-id="element-2028" data-mp="3l17r0q3rsbq9">
                More fields
                <ChevronDown size={14} className={`transition-transform duration-150 ${showExtras ? 'rotate-180' : ''}`} data-id="element-2029" data-mp="7nmjrkkj5k6l" />
              </button>

              <AnimatePresence initial={false} data-id="element-2030" data-mp="3urahaijibbkw">
                {showExtras && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.18,
                  ease: [0.23, 1, 0.32, 1]
                }} className="overflow-hidden" data-id="element-2031" data-mp="2o0474h8brum">
                    <div className="mt-3 space-y-2" data-id="element-2032" data-mp="2ftemwmj1t40p">
                      {EXTRA_FIELDS.map(field => <label key={field} className="flex items-center gap-2 text-sm text-gray-700" data-id="element-2033" data-mp="2ay92xt97hzf9">
                          <input type="checkbox" checked={extras.includes(field)} onChange={() => toggleExtra(field)} className="h-4 w-4 rounded border-gray-300 text-[#DB3725] focus:ring-[#30718D]" data-id="element-2034" data-mp="2kuvjw6yew6c0" />
                          {field}
                        </label>)}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>

          <ShareFormatPreview format={format} pathwayName={pathwayName} company={company} cohort={cohort} audience={audience} items={items} fields={selectedExtras} onExpand={() => setViewerOpen(true)} data-id="element-2035" data-mp="19fp06w68mlsm" />
        </div>

        <div className="border-t border-gray-200 p-6" data-id="element-2036" data-mp="yc0no3m74kgs">
          <AnimatePresence initial={false} data-id="element-2037" data-mp="te7h39gzdofz">
            {status === 'linked' && <motion.div initial={{
              opacity: 0,
              y: -6
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.2,
              ease: [0.23, 1, 0.32, 1]
            }} className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3" data-id="element-2038" data-mp="tglazezxculy">
                <Link2 size={16} className="text-[#30718D]" data-id="element-2039" data-mp="ao9cj3ce6dh3" />
                <code className="flex-1 truncate text-sm text-gray-700" data-id="element-2040" data-mp="almw0e7iwnmq">
                  {link}
                </code>
                <PathwayButton variant="secondary" size="sm" onClick={copyLink} data-id="element-2041" data-mp="pjw7f05912y">
                  {copied ? <Check size={15} data-id="element-2042" data-mp="3mr0yx3ejcn53" /> : <Copy size={15} data-id="element-2043" data-mp="3mriqvnu60f77" />}
                  {copied ? 'Copied' : 'Copy'}
                </PathwayButton>
                <span className="w-full text-xs text-gray-500" data-id="element-2044" data-mp="3rparr7csfbnj">
                  Anyone with the link can view it. You’ll see when the prospect
                  opens it.
                </span>
              </motion.div>}
          </AnimatePresence>

          <fieldset data-id="element-2045" data-mp="340j9p8sww9oh">
            <legend className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-2046" data-mp="3411bekycfg3s">
              Choose a format
            </legend>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5" data-id="element-2047" data-mp="38zvttlsef2nd">
              {FORMATS.map(option => {
                const Icon = option.icon;
                const active = format === option.id;
                return <label key={option.id} className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${active ? 'border-[#30718D] bg-[#30718D]/5' : 'border-gray-200 bg-white hover:bg-gray-50'}`} data-id="element-2048" data-mp="2la9v0zfxupfw">
                    <input type="radio" name="pathway-format" value={option.id} checked={active} onChange={() => setFormat(option.id)} className="mt-0.5 h-4 w-4 border-gray-300 text-[#30718D] focus:ring-[#30718D]" data-id="element-2049" data-mp="2d7hp4i1i2ql3" />
                    <span className="min-w-0" data-id="element-2050" data-mp="1uc11tm95wexl">
                      <span className="flex items-center gap-1.5 text-sm font-semibold text-gray-900" data-id="element-2051" data-mp="297h063grywzs">
                        <Icon size={14} aria-hidden="true" data-id="element-2052" data-mp="248xapt2z332o" />
                        {option.label}
                      </span>
                      <span className="mt-0.5 block text-xs text-gray-500" data-id="element-2053" data-mp="3klulp1o230cn">
                        {option.blurb}
                      </span>
                    </span>
                  </label>;
              })}
            </div>
          </fieldset>

          <div className="mt-5 flex flex-wrap items-center justify-end gap-3" data-id="element-2054" data-mp="3s655qy9o1jiw">
            <PathwayButton variant="secondary" onClick={onClose} data-id="element-2055" data-mp="3s425zmipoc0z">
              Cancel
            </PathwayButton>
            <PathwayButton onClick={runFormat} disabled={status === 'working'} className="min-w-[220px]" data-id="element-2056" data-mp="3i88yihjun8q2">
              {status === 'working' ? <Loader2 size={18} className="animate-spin" data-id="element-2057" data-mp="er8vhv4t1o96" /> : <ActiveIcon size={18} data-id="element-2058" data-mp="etcv40vo813n" />}
              {format === 'link' && status === 'linked' ? 'Regenerate Link' : activeFormat.action}
            </PathwayButton>
          </div>
        </div>
      </motion.div>
    </div>

    <DocumentViewer open={viewerOpen} pathwayName={pathwayName} company={company} cohort={cohort} audience={audience} items={items} fields={selectedExtras} onClose={() => setViewerOpen(false)} onDownloadWord={handleDownloadWord} onDownloadCsv={handleDownloadCsv} onDownloadPowerPoint={handleDownloadPowerPoint} data-id="element-2059" data-mp="spqg3jzeykmv" />

    <PrintSurface pathwayName={pathwayName} company={company} cohort={cohort} audience={audience} items={items} fields={selectedExtras} data-id="element-2060" data-mp="2w1o9lj8r8m1y" />
    </>;
}